import React, { useState } from 'react';
import { 
  BookOpen, Calendar, Download, FileText, Users, 
  Gavel, CalendarDays, Archive, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LIBRI_DISPONIBILI = [
  {
    id: 'consigli',
    titolo: 'Libro dei Consigli Direttivi',
    descrizione: 'Registro completo delle riunioni del Consiglio Direttivo con ordini del giorno, presenti e delibere.',
    icon: Gavel,
    colore: '#0EA5E9'
  },
  {
    id: 'assemblee',
    titolo: 'Libro delle Assemblee',
    descrizione: 'Registro delle Assemblee dei Soci (ordinarie e straordinarie) con verbali e delibere.',
    icon: Users,
    colore: '#8B5CF6'
  },
  {
    id: 'eventi',
    titolo: 'Libro degli Eventi',
    descrizione: 'Registro di tutte le attività, eventi e iniziative organizzate dall\'ente.',
    icon: CalendarDays,
    colore: '#22C55E'
  },
  {
    id: 'completo',
    titolo: 'Libro Attività Completo',
    descrizione: 'Registro unificato di tutte le attività dell\'anno: assemblee, consigli, eventi.',
    icon: Archive,
    colore: '#F59E0B'
  }
];

export default function LibriSociali() {
  const currentYear = new Date().getFullYear();
  const [dataInizio, setDataInizio] = useState(`${currentYear}-01-01`);
  const [dataFine, setDataFine] = useState(`${currentYear}-12-31`);
  const [loading, setLoading] = useState(null);

  const handleDownload = async (tipoLibro) => {
    if (!dataInizio || !dataFine) {
      toast.error('Seleziona le date di inizio e fine periodo');
      return;
    }

    if (dataInizio > dataFine) {
      toast.error('La data di inizio deve essere precedente alla data di fine');
      return;
    }

    setLoading(tipoLibro);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/libri-sociali/${tipoLibro}/pdf?data_inizio=${dataInizio}&data_fine=${dataFine}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        }
      );

      if (!response.ok) {
        let errorMsg = 'Errore nella generazione del PDF';
        try {
          const err = await response.json();
          errorMsg = err.detail || errorMsg;
        } catch (e) {
          errorMsg = response.status === 403 
            ? 'Questa funzionalità richiede il Piano Governance o superiore' 
            : `Errore del server (${response.status})`;
        }
        throw new Error(errorMsg);
      }

      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Libro_${tipoLibro}_${dataInizio}_${dataFine}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('PDF generato e scaricato con successo!');
    } catch (error) {
      console.error('Errore:', error);
      toast.error(error.message || 'Errore nella generazione del PDF');
    } finally {
      setLoading(null);
    }
  };

  const setAnnoCorrente = () => {
    setDataInizio(`${currentYear}-01-01`);
    setDataFine(`${currentYear}-12-31`);
  };

  const setAnnoPrecedente = () => {
    setDataInizio(`${currentYear - 1}-01-01`);
    setDataFine(`${currentYear - 1}-12-31`);
  };

  return (
    <div className="space-y-6" data-testid="libri-sociali-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <BookOpen className="w-7 h-7 text-indigo-600" />
          Libri Sociali
        </h1>
        <p className="text-slate-500">Genera i registri ufficiali dell'ente in formato PDF</p>
      </div>

      {/* Selezione Periodo */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Seleziona Periodo
          </CardTitle>
          <CardDescription>
            Definisci l'intervallo di date per i documenti da generare
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[150px]">
              <Label htmlFor="data_inizio">Data Inizio</Label>
              <Input
                id="data_inizio"
                type="date"
                value={dataInizio}
                onChange={(e) => setDataInizio(e.target.value)}
              />
            </div>
            <div className="flex-1 min-w-[150px]">
              <Label htmlFor="data_fine">Data Fine</Label>
              <Input
                id="data_fine"
                type="date"
                value={dataFine}
                onChange={(e) => setDataFine(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={setAnnoCorrente}>
                Anno {currentYear}
              </Button>
              <Button variant="outline" size="sm" onClick={setAnnoPrecedente}>
                Anno {currentYear - 1}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grid Libri Disponibili */}
      <div className="grid md:grid-cols-2 gap-4">
        {LIBRI_DISPONIBILI.map((libro) => {
          const Icon = libro.icon;
          const isLoading = loading === libro.id;
          
          return (
            <Card 
              key={libro.id} 
              className="hover:shadow-md transition-shadow border-l-4"
              style={{ borderLeftColor: libro.colore }}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${libro.colore}20` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: libro.colore }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800">{libro.titolo}</h3>
                    <p className="text-sm text-slate-500 mt-1">{libro.descrizione}</p>
                    <Button 
                      className="mt-4"
                      onClick={() => handleDownload(libro.id)}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generazione...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Scarica PDF
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-800">Informazioni sui Libri Sociali</h4>
              <p className="text-sm text-blue-700 mt-1">
                I libri sociali sono registri obbligatori per le associazioni. Contengono la documentazione 
                delle decisioni prese dagli organi sociali e devono essere conservati presso la sede dell'ente.
              </p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1">
                <li>• <strong>Libro Assemblee:</strong> Verbali delle assemblee dei soci</li>
                <li>• <strong>Libro Consigli:</strong> Verbali delle riunioni del Consiglio Direttivo</li>
                <li>• <strong>Libro Eventi:</strong> Registro delle attività svolte (utile per rendicontazione)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
